"use client";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  fadeUp,
  stagger,
  cardItem,
  slideLeft,
  viewportOnce,
} from "../components/motionVariants";
import {
  Target,
  ClipboardList,
  IndianRupee,
  RefreshCw,
  Settings,
  Handshake,
  Mail,
  Clock,
  MapPin,
  MessageCircle,
  Lock,
  Zap,
  Factory,
  ShoppingCart,
  Users,
  Briefcase,
  Globe,
  Store,
  CheckCircle,
  ChevronDown,
} from "lucide-react";

// ─── What we help with ───────────────────────────────────────────────
const HELP_ITEMS = [
  { Icon: Target, label: "Book a product demo (Books or CRM)" },
  { Icon: ClipboardList, label: "Questions about GST compliance in ABSS" },
  { Icon: IndianRupee, label: "Pricing & plan selection for your team size" },
  { Icon: RefreshCw, label: "Migrate data from Tally, Vyapar, or Excel" },
  { Icon: Settings, label: "Technical support & configuration help" },
  { Icon: Handshake, label: "Partnership or reseller inquiries" },
];

// ─── What happens next ───────────────────────────────────────────────
const NEXT_STEPS = [
  {
    step: "01",
    title: "We receive your inquiry",
    desc: "Your message lands directly with our India-based sales or support team - not a chatbot, not an overseas call centre.",
  },
  {
    step: "02",
    title: "We reach out within 1 business day",
    desc: "For demo requests, we'll propose a 30-minute call. For support tickets, we'll reply via email or WhatsApp with an answer or a fix.",
  },
  {
    step: "03",
    title: "You get onboarded at your pace",
    desc: "No pressure. If you're ready, we'll onboard you same week. If you need more time, we'll send you resources and check back in.",
  },
];

// ─── Office & contact info ────────────────────────────────────────────
const CONTACT_DETAILS = [
  {
    Icon: Mail,
    label: "Email",
    value: "support@abss.ai",
    href: "mailto:support@abss.ai",
  },
  {
    Icon: MessageCircle,
    label: "WhatsApp",
    value: "+91 74358 09525",
    href: "https://wa.me/917435809525",
  },
  {
    Icon: Clock,
    label: "Support Hours",
    value: "Mon–Fri, 10 AM–6 PM IST",
    href: null,
  },
  {
    Icon: MapPin,
    label: "Based in",
    value: "Ahmedabad, Gujarat · Adaptive Business Support Systems LLP",
    href: null,
  },
];

// ─── Trust pills ─────────────────────────────────────────────────────
const TRUST_PILLS = [
  { Icon: Lock, label: "Data stays in India" },
  { Icon: MapPin, label: "India-based support team" },
  { Icon: Zap, label: "Reply within 1 business day" },
];

// ─── Business type cards ─────────────────────────────────────────────
const BUSINESS_CARDS = [
  {
    Icon: Factory,
    title: "Manufacturers & Processors",
    desc: "You need production tracking (Bill of Materials), multi-warehouse inventory, and GST-compliant invoicing - all linked. ABSS Books handles this end-to-end.",
    cta: "Book a Manufacturing Demo",
    topic: "DEMO_BOOKS",
  },
  {
    Icon: ShoppingCart,
    title: "Traders & Distributors",
    desc: "You deal with high invoice volumes, multiple vendors, and need your debtors/creditors aging to be always current. ABSS Books + CRM keeps it all in one place.",
    cta: "Book a Trading Business Demo",
    topic: "DEMO_BOTH",
  },
  {
    Icon: Users,
    title: "Field Sales Teams",
    desc: "Your reps are at trade shows, client visits, and expos. QR lead capture, automatic lead assignment, and the ABSS mobile app are built for exactly this.",
    cta: "Book a CRM Demo",
    topic: "DEMO_CRM",
  },
  {
    Icon: Briefcase,
    title: "Service Businesses",
    desc: "Recurring invoices, client payment tracking, and credit notes. ABSS Books handles SAC codes and service GST correctly.",
    cta: "Book a Service Business Demo",
    topic: "DEMO_BOOKS",
  },
  {
    Icon: Globe,
    title: "Exporters & Import–Export Firms",
    desc: "Multi-currency invoicing with exchange rates, correct IGST handling, and E-Way Bill for goods moving to port - ABSS Books has your compliance covered.",
    cta: "Book an Export Business Demo",
    topic: "DEMO_BOOKS",
  },
  {
    Icon: Store,
    title: "Multi-Branch Businesses",
    desc: "Manage stock, sales, and P&L across multiple locations from one account. Branch-wise reports, shared item master, and centralised billing.",
    cta: "Book a Multi-Branch Demo",
    topic: "DEMO_BOOKS",
  },
];

const CONTACT_FAQS = [
  {
    question: "Is there a free trial?",
    answer:
      "Yes - every new ABSS account starts with a 14-day free trial, no credit card required. After 14 days, choose the plan that fits your business.",
  },
  {
    question: "How long does onboarding take?",
    answer:
      "Most businesses are live within 1–3 days. If you're migrating from Tally, our team does the data migration - typically 2–5 business days depending on volume.",
  },
  {
    question: "Do you support GST and E-Invoice out of the box?",
    answer:
      "Yes. GST invoicing is built into every plan - no plugins. E-Invoice (IRN) and E-Way Bill are included on selected plans; see the pricing page for details.",
  },
  {
    question: "Can I use ABSS CRM and Books separately?",
    answer:
      "Yes. Books and CRM are separate products that can be used independently. Use both and they share the same customer and item data. You can start with one and add the other later.",
  },
  {
    question: "What languages is ABSS support available in?",
    answer:
      "Our support team communicates in English, Hindi, and Gujarati.",
  },
];

const sectionPadding = "px-5 sm:px-6 md:px-10 lg:px-12";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    chooseTopic: "",
    helpMessage: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [toast, setToast] = useState({
    open: false,
    type: "success",
    message: "",
  });
  const [activeFaqIndex, setActiveFaqIndex] = useState(null);

  const showToast = (type, message) => {
    setToast({ open: true, type, message });
    window.setTimeout(() => setToast((t) => ({ ...t, open: false })), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      showToast("error", "Name is required.");
      return;
    }
    if (!formData.email.trim()) {
      showToast("error", "Email is required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      showToast("error", "Please enter a valid email address.");
      return;
    }
    if (!formData.phoneNumber.trim()) {
      showToast("error", "Phone number is required.");
      return;
    }
    if (!formData.helpMessage.trim()) {
      showToast("error", "Please tell us how we can help you.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = { ...formData, source: "ABSS" };
      // chooseTopic is optional; omit it entirely when nothing is selected
      // (an empty string is not a valid enum value on the backend).
      if (!payload.chooseTopic) {
        delete payload.chooseTopic;
      }

      const response = await fetch(
        "https://api.admin.abss.ai/api/v1/inquiry/submit",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok)
        throw new Error(`Request failed with status ${response.status}`);

      const result = await response.json();
      setFormData({
        name: "",
        email: "",
        phoneNumber: "",
        chooseTopic: "",
        helpMessage: "",
      });
      setSubmitted(true);
      showToast(
        "success",
        typeof result?.message === "string"
          ? result.message
          : "Thanks! Your message has been submitted successfully.",
      );
    } catch (error) {
      console.error("Error submitting contact form:", error);
      showToast(
        "error",
        "Something went wrong. Please try again or email us at support@abss.ai",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="overflow-x-hidden w-full min-h-screen bg-white">
      {/* ── TOAST ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {toast.open && (
          <motion.div
            initial={{ opacity: 0, y: -16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            role="status"
            aria-live="polite"
            className="fixed top-4 left-1/2 z-[9999] w-full max-w-xl -translate-x-1/2 px-5 sm:px-4"
          >
            <div className="flex items-center gap-3 bg-white border border-[#E5E5E5] rounded-[12px] px-5 py-4 shadow-lg">
              <div
                className={`flex h-9 w-9 items-center justify-center rounded-[8px] flex-shrink-0 ${toast.type === "success" ? "bg-[#F0FDF4] text-[#16A34A]" : "bg-[#FEF2F2] text-[#DC2626]"}`}
              >
                {toast.type === "success" ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="geist-medium text-sm text-[#171717]">
                  {toast.type === "success" ? "Message sent" : "Error"}
                </p>
                <p className="geist-medium text-xs text-[#737373] mt-0.5">
                  {toast.message}
                </p>
              </div>
              <button
                onClick={() => setToast((t) => ({ ...t, open: false }))}
                className="ml-2 text-[#A3A3A3] hover:text-[#171717] transition-colors"
                aria-label="Close"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── HERO ─────────────────────────────────────────────────── */}
      <section className="relative w-full bg-white border-b border-[#E5E5E5] overflow-hidden">
        <div className="absolute inset-0 z-0 select-none pointer-events-none">
          <div className="absolute top-[-25%] left-[-15%] w-[60%] h-[80%] rounded-full bg-gradient-to-br from-orange-200/40 to-amber-100/10 blur-[130px] animate-aurora-pulse" />
          <div className="absolute bottom-[-15%] right-[-10%] w-[55%] h-[75%] rounded-full bg-gradient-to-tr from-blue-200/30 to-indigo-100/10 blur-[130px] animate-aurora-pulse [animation-delay:3s]" />
          <div className="absolute inset-0 bg-grid-mesh opacity-80" />
        </div>

        <div
          className={`relative z-10 max-w-[1160px] mx-auto pt-24 sm:pt-28 md:pt-32 pb-10 sm:pb-16 lg:pb-20 ${sectionPadding} min-w-0`}
        >
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-start min-w-0">
            {/* ── LEFT: Info ─────────────────────────────────────── */}
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="show"
              className="w-full min-w-0 text-center md:text-left flex flex-col items-center md:items-start"
            >
              <motion.div variants={fadeUp} className="mb-5 sm:mb-6">
                <span className="inline-flex items-center gap-1.5 border border-[#e5e5e5] rounded-full px-3.5 sm:px-4 py-1.5 text-[12px] sm:text-[13px] text-[#262626] geist-semibold bg-white/80 backdrop-blur-sm shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-[#DF5D01] inline-block animate-pulse"></span>
                  Get in Touch
                </span>
              </motion.div>
              <motion.h1
                variants={fadeUp}
                className="text-[26px] sm:text-[42px] md:text-[48px] leading-[1.15] sm:leading-[1.12] text-[#171717] mb-4 sm:mb-6 sentient-bold tracking-tight px-1"
              >
                Talk to a Real Person, Not a Bot
              </motion.h1>
              <motion.p
                variants={fadeUp}
                className="text-[15px] sm:text-lg md:text-[19px] font-medium leading-relaxed text-[#262626] mb-7 sm:mb-10 geist-medium max-w-xl px-1"
              >
                Whether you want a product demo, have a GST question, or need
                help migrating from Tally - our India-based team responds within
                1 business day.
              </motion.p>

              {/* What we help with */}
              <motion.div
                variants={stagger}
                className="mb-7 sm:mb-10 w-full max-w-[360px] md:max-w-none mx-auto md:mx-0 text-left"
              >
                <motion.p
                  variants={fadeUp}
                  className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-3 sm:mb-4 text-center md:text-left"
                >
                  We can help you with
                </motion.p>
                <div className="space-y-2.5 sm:space-y-3">
                  {HELP_ITEMS.map((item, i) => (
                    <motion.div
                      key={i}
                      variants={fadeUp}
                      className="flex items-start sm:items-center gap-2.5 sm:gap-3 min-w-0"
                    >
                      <div className="w-7 h-7 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] flex items-center justify-center flex-shrink-0 shadow-sm mt-0.5 sm:mt-0">
                        <item.Icon className="w-3.5 h-3.5 text-[#525252]" />
                      </div>
                      <span className="geist-medium text-[13px] sm:text-sm text-[#333333] min-w-0">
                        {item.label}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Contact details */}
              <motion.div
                variants={stagger}
                className="border-t border-[#E5E5E5] pt-6 sm:pt-8 space-y-3 sm:space-y-4 w-full max-w-[360px] md:max-w-none mx-auto md:mx-0"
              >
                {CONTACT_DETAILS.map((detail, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-left min-w-0"
                  >
                    <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                      <div className="w-7 h-7 rounded-lg bg-[#F5F5F5] border border-[#E5E5E5] flex items-center justify-center flex-shrink-0 shadow-sm">
                        <detail.Icon className="w-3.5 h-3.5 text-[#525252]" />
                      </div>
                      <span className="geist-medium text-xs text-[#737373] sm:w-24 flex-shrink-0">
                        {detail.label}
                      </span>
                    </div>
                    {detail.href ? (
                      <a
                        href={detail.href}
                        {...(detail.href.startsWith("http")
                          ? { target: "_blank", rel: "noopener noreferrer" }
                          : {})}
                        className="geist-semibold text-sm text-[#171717] hover:text-black transition-colors pl-9 sm:pl-0 break-words"
                      >
                        {detail.value}
                      </a>
                    ) : (
                      <span className="geist-medium text-sm text-[#525252] pl-9 sm:pl-0 break-words">
                        {detail.value}
                      </span>
                    )}
                  </motion.div>
                ))}
              </motion.div>

              {/* Trust pills */}
              <motion.div
                variants={fadeUp}
                className="flex flex-wrap justify-center md:justify-start gap-2 sm:gap-2.5 mt-6 sm:mt-8 w-full"
              >
                {TRUST_PILLS.map((pill, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1.5 geist-medium text-xs sm:text-[13px] text-[#262626] px-3.5 py-1.5 border border-[#E5E5E5] bg-white/70 backdrop-blur-sm rounded-full shadow-sm"
                  >
                    <pill.Icon className="w-3 h-3 text-[#737373]" />
                    {pill.label}
                  </span>
                ))}
              </motion.div>
            </motion.div>

            {/* ── RIGHT: Form ────────────────────────────────────── */}
            <motion.div
              variants={slideLeft}
              initial="hidden"
              animate="show"
              className="w-full min-w-0"
            >
              <AnimatePresence mode="wait">
                {submitted ? (
                  /* Success State */
                  <motion.div
                    key="success"
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="border border-[#E5E5E5] bg-white rounded-xl sm:rounded-2xl p-6 sm:p-12 text-center shadow-md sm:shadow-lg"
                  >
                    <div className="w-12 h-12 sm:w-14 sm:h-14 bg-[#F0FDF4] rounded-xl flex items-center justify-center mx-auto mb-5 sm:mb-6 shadow-sm">
                      <CheckCircle className="w-6 h-6 sm:w-7 sm:h-7 text-[#16A34A]" />
                    </div>
                    <h3 className="text-lg sm:text-2xl text-[#171717] mb-2.5 sm:mb-3 sentient-medium tracking-tight">
                      We&apos;ve got your message
                    </h3>
                    <p className="geist-medium text-[13px] sm:text-sm text-[#525252] mb-5 sm:mb-6 leading-relaxed max-w-sm mx-auto">
                      Our team will reach out within 1 business day. If
                      it&apos;s urgent, WhatsApp us directly at the number on
                      the left.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="geist-semibold text-sm text-[#171717] hover:text-black transition-colors"
                    >
                      Send another message →
                    </button>
                  </motion.div>
                ) : (
                  /* Form */
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="border border-[#E5E5E5] bg-white rounded-xl sm:rounded-2xl p-4 sm:p-8 shadow-md sm:shadow-lg min-w-0"
                  >
                    <p className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-5 sm:mb-6 text-center sm:text-left">
                      Send us a message
                    </p>

                    <form
                      id="contact-form"
                      className="space-y-4 sm:space-y-5"
                      onSubmit={handleSubmit}
                    >
                      {/* Name + Email */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block geist-medium text-xs text-[#171717] mb-2">
                            Full Name <span className="text-[#DF5D01]">*</span>
                          </label>
                          <input
                            type="text"
                            placeholder="Your full name"
                            value={formData.name}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                name: e.target.value,
                              }))
                            }
                            className="w-full h-11 px-3 border border-[#E5E5E5] rounded-lg text-sm geist-medium text-[#171717] placeholder-[#A3A3A3] focus:outline-none focus:border-[#171717] transition-colors bg-[#FAFAFA]"
                          />
                        </div>
                        <div>
                          <label className="block geist-medium text-xs text-[#171717] mb-2">
                            Work Email <span className="text-[#DF5D01]">*</span>
                          </label>
                          <input
                            type="email"
                            placeholder="you@company.com"
                            value={formData.email}
                            onChange={(e) =>
                              setFormData((p) => ({
                                ...p,
                                email: e.target.value,
                              }))
                            }
                            className="w-full h-11 px-3 border border-[#E5E5E5] rounded-lg text-sm geist-medium text-[#171717] placeholder-[#A3A3A3] focus:outline-none focus:border-[#171717] transition-colors bg-[#FAFAFA]"
                          />
                        </div>
                      </div>

                      {/* Phone */}
                      <div>
                        <label className="block geist-medium text-xs text-[#171717] mb-2">
                          Phone Number <span className="text-[#DF5D01]">*</span>
                        </label>
                        <input
                          type="tel"
                          placeholder="+91 98765 43210"
                          value={formData.phoneNumber}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              phoneNumber: e.target.value,
                            }))
                          }
                          className="w-full h-11 px-3 border border-[#E5E5E5] rounded-lg text-sm geist-medium text-[#171717] placeholder-[#A3A3A3] focus:outline-none focus:border-[#171717] transition-colors bg-[#FAFAFA]"
                        />
                      </div>

                      {/* Topic */}
                      <div>
                        <label className="block geist-medium text-xs text-[#171717] mb-2">
                          What are you reaching out about?
                        </label>
                        <select
                          value={formData.chooseTopic}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              chooseTopic: e.target.value,
                            }))
                          }
                          className="w-full h-11 px-3 border border-[#E5E5E5] rounded-lg text-sm geist-medium text-[#171717] bg-[#FAFAFA] focus:outline-none focus:border-[#171717] transition-colors appearance-none"
                        >
                          <option value="">Select a topic…</option>
                          <optgroup label="Demo Requests">
                            <option value="DEMO_BOOKS">
                              Book a Demo - ABSS Books
                            </option>
                            <option value="DEMO_CRM">
                              Book a Demo - ABSS CRM
                            </option>
                            <option value="DEMO_BOTH">
                              Book a Demo - Full Platform
                            </option>
                          </optgroup>
                          <optgroup label="Sales & Pricing">
                            <option value="SALES_INQUIRY">Sales Inquiry</option>
                            <option value="PRICING_QUESTION">
                              Pricing Question
                            </option>
                            <option value="ENTERPRISE_PLAN">
                              Enterprise / Custom Plan
                            </option>
                          </optgroup>
                          <optgroup label="Onboarding">
                            <option value="DATA_MIGRATION">
                              Migrate from Tally / Excel
                            </option>
                            <option value="ONBOARDING_HELP">
                              Onboarding & Setup Help
                            </option>
                            <option value="GST_SETUP">
                              GST / E-Invoice Configuration
                            </option>
                          </optgroup>
                          <optgroup label="Support">
                            <option value="TECHNICAL_SUPPORT">
                              Technical Support
                            </option>
                            <option value="BILLING">
                              Billing & Subscription Issue
                            </option>
                          </optgroup>
                          <optgroup label="Other">
                            <option value="EARLY_ACCESS">
                              Early Access (ABSS Teams)
                            </option>
                            <option value="PARTNERSHIP">
                              Partnership / Reseller Inquiry
                            </option>
                            <option value="GENERAL">General Query</option>
                          </optgroup>
                        </select>
                      </div>

                      {/* Message */}
                      <div>
                        <label className="block geist-medium text-xs text-[#171717] mb-2">
                          How can we help?{" "}
                          <span className="text-[#DF5D01]">*</span>
                        </label>
                        <textarea
                          rows={5}
                          placeholder="Tell us about your business, your current tools, and what you're looking to achieve with ABSS…"
                          value={formData.helpMessage}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              helpMessage: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-3 border border-[#E5E5E5] rounded-lg text-sm geist-medium text-[#171717] placeholder-[#A3A3A3] focus:outline-none focus:border-[#171717] transition-colors bg-[#FAFAFA] resize-none"
                        />
                      </div>

                      {/* Submit */}
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full h-[46px] bg-[#171717] text-white geist-semibold text-sm hover:bg-black rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="animate-spin"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                            </svg>
                            Sending…
                          </>
                        ) : (
                          "Send Message →"
                        )}
                      </button>

                      <p className="geist-medium text-xs text-[#737373] text-center">
                        By submitting, you agree to our{" "}
                        <a
                          href="/privacy-policy"
                          className="underline hover:text-[#525252]"
                        >
                          Privacy Policy
                        </a>
                        . We never share your data with third parties.
                      </p>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── WHAT HAPPENS NEXT ────────────────────────────────────── */}
      <section className="w-full bg-white border-b border-[#E5E5E5] overflow-hidden">
        <div
          className={`max-w-[1160px] mx-auto ${sectionPadding} py-10 sm:py-16 md:py-20 min-w-0`}
        >
          <motion.div
            className="text-center mb-8 sm:mb-14 px-1"
            variants={stagger}
            {...viewportOnce}
          >
            <motion.p
              variants={fadeUp}
              className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-2.5 sm:mb-3"
            >
              After You Submit
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="text-[22px] sm:text-[32px] md:text-[38px] leading-[1.25] sm:leading-[1.2] text-[#171717] sentient-medium tracking-tight"
            >
              Here&apos;s What to Expect
            </motion.h2>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-3 gap-4 sm:gap-6 min-w-0"
            variants={stagger}
            {...viewportOnce}
          >
            {NEXT_STEPS.map((step, i) => (
              <motion.div
                key={i}
                variants={cardItem}
                className="border border-[#E5E5E5] rounded-xl sm:rounded-2xl p-4 sm:p-8 bg-white shadow-sm hover:shadow-lg hover:border-neutral-300/80 transition-all duration-300 min-w-0 text-center md:text-left"
              >
                <div className="w-10 h-10 bg-[#171717] text-white geist-semibold text-xs rounded-lg flex items-center justify-center mb-4 sm:mb-6 shadow-sm mx-auto md:mx-0">
                  {step.step}
                </div>
                <h3 className="text-[16px] sm:text-xl text-[#171717] mb-2.5 sm:mb-3 sentient-medium tracking-tight">
                  {step.title}
                </h3>
                <p className="text-[#333333] text-[13px] sm:text-[15px] leading-relaxed geist-medium">
                  {step.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── WHO REACHES OUT ──────────────────────────────────────── */}
      <section className="w-full bg-[#f5f5f5] border-b border-[#E5E5E5] overflow-hidden">
        <div
          className={`max-w-[1160px] mx-auto ${sectionPadding} py-10 sm:py-16 md:py-20 min-w-0`}
        >
          <motion.div
            className="text-center mb-8 sm:mb-14 px-1"
            variants={stagger}
            {...viewportOnce}
          >
            <motion.p
              variants={fadeUp}
              className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-2.5 sm:mb-3"
            >
              Who We Work With
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="text-[22px] sm:text-[32px] md:text-[38px] leading-[1.25] sm:leading-[1.2] text-[#171717] mb-3 sm:mb-4 sentient-medium tracking-tight"
            >
              ABSS Is Built for These Businesses
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="text-[14px] sm:text-lg text-[#262626] max-w-xl mx-auto geist-medium leading-relaxed px-1"
            >
              If you recognise your business below, we&apos;re the right fit.
              Reach out and we&apos;ll show you exactly how.
            </motion.p>
          </motion.div>

          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 min-w-0"
            variants={stagger}
            {...viewportOnce}
          >
            {BUSINESS_CARDS.map((card, i) => (
              <motion.div
                key={i}
                variants={cardItem}
                className="bg-white border border-[#E5E5E5]/80 rounded-xl sm:rounded-2xl p-4 sm:p-8 flex flex-col shadow-sm hover:shadow-lg hover:border-neutral-300/80 transition-all duration-300 min-w-0 text-center sm:text-left items-center sm:items-start"
              >
                <div className="w-10 h-10 rounded-xl bg-[#FAFAFA] border border-[#E5E5E5] flex items-center justify-center mb-4 sm:mb-5 flex-shrink-0 shadow-sm">
                  <card.Icon className="w-5 h-5 text-[#525252]" />
                </div>
                <h3 className="text-[16px] sm:text-xl text-[#171717] mb-2.5 sm:mb-3 sentient-medium tracking-tight">
                  {card.title}
                </h3>
                <p className="text-[#333333] text-[13px] sm:text-[15px] leading-relaxed geist-medium mb-5 sm:mb-6 flex-1 max-w-[320px] sm:max-w-none">
                  {card.desc}
                </p>
                <a
                  href="#contact-form"
                  onClick={(e) => {
                    e.preventDefault();
                    setFormData((p) => ({ ...p, chooseTopic: card.topic }));
                    document
                      .getElementById("contact-form")
                      ?.scrollIntoView({ behavior: "smooth", block: "center" });
                  }}
                  className="geist-semibold text-sm text-[#171717] hover:text-black transition-colors"
                >
                  {card.cta} →
                </a>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── FREQUENTLY ASKED BEFORE CONTACTING ───────────────────── */}
      <section className="w-full bg-white border-b border-[#E5E5E5] overflow-hidden">
        <div
          className={`max-w-[1160px] mx-auto ${sectionPadding} py-10 sm:py-16 md:py-20 min-w-0`}
        >
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 min-w-0">
            <motion.div
              variants={stagger}
              {...viewportOnce}
              className="text-center md:text-left flex flex-col items-center md:items-start min-w-0"
            >
              <motion.p
                variants={fadeUp}
                className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-2.5 sm:mb-3"
              >
                Before You Write
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="text-[22px] sm:text-[32px] md:text-[38px] leading-[1.25] sm:leading-[1.2] text-[#171717] mb-4 sm:mb-6 sentient-medium tracking-tight max-w-[520px]"
              >
                Quick Answers to Common Questions
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="text-[#333333] text-[13px] sm:text-[15px] md:text-[16px] leading-relaxed geist-medium max-w-[480px]"
              >
                These might save you a trip to the form. If not - we&apos;re
                happy to hear from you.
              </motion.p>
            </motion.div>

            <motion.div
              variants={stagger}
              {...viewportOnce}
              className="space-y-3 sm:space-y-4 min-w-0 w-full"
            >
              {CONTACT_FAQS.map((faq, i) => {
                const isOpen = activeFaqIndex === i;
                return (
                  <motion.div
                    key={faq.question}
                    variants={fadeUp}
                    className={`border rounded-xl sm:rounded-2xl bg-white overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.02)] transition-all duration-300 min-w-0 ${
                      isOpen
                        ? "border-[#DF5D01]"
                        : "border-neutral-200 hover:border-neutral-300"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() => setActiveFaqIndex(isOpen ? null : i)}
                      className="w-full flex justify-between items-start sm:items-center text-left px-4 sm:px-6 py-4 sm:py-5 gap-3 sm:gap-6"
                      aria-expanded={isOpen}
                    >
                      <span className="geist-semibold text-[13px] sm:text-[14px] text-neutral-800 leading-snug min-w-0">
                        {faq.question}
                      </span>
                      <span
                        className={`flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center border transition-all duration-300 ${
                          isOpen
                            ? "bg-orange-50 border-orange-100 text-[#DF5D01] rotate-180"
                            : "bg-neutral-50 border-neutral-200 text-neutral-500"
                        }`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </span>
                    </button>
                    <AnimatePresence initial={false}>
                      {isOpen && (
                        <motion.div
                          key="content"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.25, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <p className="px-4 sm:px-6 pb-4 sm:pb-6 geist-medium text-[12px] sm:text-[13px] text-neutral-500 leading-relaxed border-t border-neutral-100 pt-3 sm:pt-4 mt-0 bg-neutral-50/30">
                            {faq.answer}
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
